import React, { useState, useEffect, useCallback } from 'react';
import { FolderOpen, FileText, Eye, Trash2, RefreshCw } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { documentAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { BilagViewer } from '../../components/documents/BilagViewer';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

// Accountant "Other Documents" area — non-invoice files clients (or the firm) send.
// NOT reviewed/approved/booked; just delivered + filed. View / download / delete.
export default function FilesPage() {
    const { currentTenant } = useTenant();
    const { isFirmOwner } = useAuth();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewDoc, setViewDoc] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchDocs = useCallback(async () => {
        if (!currentTenant || !isFirmOwner) { setLoading(false); return; }
        try {
            setLoading(true);
            const res = await documentAPI.getAll(currentTenant.id, null, 'other');
            setDocs(res.data || []);
        } catch (err) {
            console.error('Failed to load files:', err);
        } finally {
            setLoading(false);
        }
    }, [currentTenant, isFirmOwner]);

    useEffect(() => { fetchDocs(); }, [fetchDocs]);

    const handleDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await documentAPI.deleteDocument(toDelete.id);
            toast.success('Document deleted');
            setToDelete(null);
            fetchDocs();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete document');
        } finally {
            setDeleting(false);
        }
    };

    // Files (non-invoice documents) are accountant-only — firm owner + admin, not staff.
    if (!isFirmOwner) {
        return (
            <div className="space-y-6" data-testid="files-page">
                <h1 className="font-heading text-3xl font-bold">Files</h1>
                <Card className="border-dashed border-2">
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                        <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">Files are only available to the firm owner.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!currentTenant) {
        return (
            <div className="space-y-6" data-testid="files-page">
                <h1 className="font-heading text-3xl font-bold">Files</h1>
                <Card className="border-dashed border-2">
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                        <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">Select a company to see its files.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="files-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <FolderOpen className="h-7 w-7 text-primary" /> Files
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Non-invoice documents for {currentTenant.name} — contracts, letters (SKAT), bank docs, agreements. Not part of the invoice flow.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDocs}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Other documents ({docs.length})</CardTitle>
                    <CardDescription>Delivered files — view, download, or remove. These never enter invoice review/reconciliation.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : docs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <FileText className="h-10 w-10 text-muted-foreground mb-3" />
                            <p className="text-muted-foreground">No other documents for this company yet.</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {docs.map((d) => (
                                <div key={d.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 px-5 py-3" data-testid={`file-row-${d.id}`}>
                                    <div className="min-w-0">
                                        <p className="font-medium truncate flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" /> {d.filename || 'Document'}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5">Received {formatDate(d.created_at)}</p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Button variant="ghost" size="sm" onClick={() => setViewDoc(d)} data-testid={`view-file-${d.id}`}>
                                            <Eye className="h-4 w-4 mr-1" /> View
                                        </Button>
                                        <Button
                                            variant="ghost" size="icon"
                                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                            onClick={() => setToDelete(d)} title="Delete"
                                            data-testid={`delete-file-${d.id}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!viewDoc} onOpenChange={(o) => { if (!o) setViewDoc(null); }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> {viewDoc?.filename || 'Document'}</DialogTitle>
                    </DialogHeader>
                    {viewDoc && <BilagViewer docId={viewDoc.id} filename={viewDoc.filename}
                                             fileType={viewDoc.file_type} className="h-[70vh] border rounded-lg" />}
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this document?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {toDelete && (<>This removes <span className="font-medium">{toDelete.filename}</span> from Files and the client's inbox. This cannot be undone.</>)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); handleDelete(); }} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-file">
                            {deleting ? 'Deleting…' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
